import {
  AIStarsIcon,
  Button,
  DebtRepaymentProjection,
  DebtRepaymentService,
  DebtRepaymentSuggestion,
  Disclaimer,
  Div,
  H5,
  Icon,
  LoadingDots,
  ModalPortal,
  P,
  Tab,
  TabsWrapper,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { SERVICE_PLAN_TITLES } from '../helpers/repaymentPlanLabels';
import {
  describeSuggestionError,
  formatPeriod,
  hasAnyMethod,
  targetMonthName,
} from '../helpers/repaymentSuggestionLabels';
import { SuggestedPlan } from './SuggestedPlan';
import { SuggestionComparison } from './SuggestionComparison';
import { SuggestionDetails } from './SuggestionDetails';

type Props = {
  isOpen: boolean;
  /** Which plan it suggests, for the title. */
  service: DebtRepaymentService;
  closeModal: () => void;
  /** True from the click until the answer or an error lands. */
  isGenerating: boolean;
  data: DebtRepaymentSuggestion | undefined;
  error: unknown;
  onRetry: () => void;
  /** Fills the plan form. Nothing is saved until the form's own button. */
  onApply: (projection: DebtRepaymentProjection) => void;
};

type Option = 'A' | 'B';

const OPTIONS: Option[] = ['A', 'B'];

const OPTION_NOTES: Record<Option, string> = {
  A: 'Any mix of the vPay Card, per order and per delivery.',
  B: 'Least noticeable: only the vPay Card, a small amount a day the owner barely notices.',
};

/**
 * The option on show, as the parts the modal lays out. Option B can be there
 * without a plan (nothing lands on the vPay Card, or its AI failed): its
 * adjustments then say why.
 */
const optionParts = (data: DebtRepaymentSuggestion, option: Option) => {
  if (option === 'B' && data.optionB)
    return {
      projection: data.optionB.suggestion,
      ai: data.optionB.ai,
      adjustments: data.optionB.adjustments,
      comparison: undefined,
    };
  return {
    projection: data.suggestion,
    ai: data.ai,
    adjustments: data.adjustments,
    comparison: data.ai.comparison,
  };
};

/**
 * The AI's repayment plan, opened from the plan form (hence the portal, which
 * stacks it above that modal). A backend from Oct 22, 2026 on answers with
 * two: option A (any mix of methods) and option B (the vPay Card alone, a
 * small amount a day); a tab switches between them and Apply takes the one
 * on show.
 *
 * `glow` is passed on every render on purpose: the modal mounts its glow
 * wrapper whenever the prop is present, so the border fades out when the
 * answer lands instead of the whole modal remounting (see modal.tsx).
 */
export const RepaymentSuggestionModal = ({
  isOpen,
  service,
  closeModal,
  isGenerating,
  data,
  error,
  onRetry,
  onApply,
}: Props) => {
  const errorInfo =
    !isGenerating && error ? describeSuggestionError(error) : null;
  const result = !isGenerating && !errorInfo ? data : undefined;
  const [option, setOption] = useState<Option>('A');
  // Every answer opens on option A.
  useEffect(() => setOption('A'), [data]);
  const hasOptionB = !!result?.optionB;
  const parts = result ? optionParts(result, option) : null;
  const projection = parts?.projection ?? null;
  // A suggestion with every method off (nothing to collect from) has nothing
  // to put in the form.
  const canApply = !!projection && hasAnyMethod(projection);

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      textAlign="left"
      width="640px"
      glow={isGenerating}
      $numOfFloatingButtons={1}
      footer={
        canApply ? (
          <Button
            $selected
            $w="100%"
            $wSM="100%"
            onClick={() => onApply(projection)}
          >
            {hasOptionB ? `Apply option ${option} to plan` : 'Apply to plan'}
          </Button>
        ) : undefined
      }
    >
      <Div $flex $alignCenter $gap="8px">
        <Icon>
          <AIStarsIcon />
        </Icon>
        <H5 $m="0">{SERVICE_PLAN_TITLES[service]} suggestion</H5>
      </Div>
      {result && (
        <Disclaimer $m="4px 0 0" $color={lightTheme.lightGray}>
          {[
            `Based on ${formatPeriod(result.periodFrom, result.periodTo)}`,
            `${displayCentsInDollars(result.remainingDebt)} to repay`,
            result.targetMonths != null &&
              `repay by ${targetMonthName(result.targetMonths)}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Disclaimer>
      )}

      <Div $mt="16">
        {isGenerating ? (
          <Div $m="8px 0">
            {/* The dots are a span, which text paints red unless told. */}
            <P
              $m="0"
              $center
              $color={lightTheme.lightGray}
              $spanColor={lightTheme.lightGray}
            >
              Working out a plan
              <LoadingDots active />
            </P>
            <Disclaimer $m="4px 0 0" $center $color={lightTheme.lightGray}>
              This usually takes a few seconds.
            </Disclaimer>
          </Div>
        ) : errorInfo ? (
          <>
            <P $m="0">{errorInfo.message}</P>
            {errorInfo.canRetry && (
              <Button
                $blueGradient
                $selected
                $w="100%"
                $wSM="100%"
                $mt="16"
                onClick={onRetry}
              >
                Try again
              </Button>
            )}
          </>
        ) : result && parts ? (
          <Div $flex $column $gap="16px">
            {hasOptionB && (
              <div>
                {/* Room for the selected tab's shadow, which the wrapper's
                    scrolling would clip. */}
                <TabsWrapper
                  style={{
                    padding: '4px 24px 40px',
                    margin: '-4px -24px -40px',
                    gap: 8,
                  }}
                >
                  {OPTIONS.map((item) => (
                    <Tab
                      key={item}
                      // Half each, less half the 8px gap: 50% overflowed.
                      $width="calc(50% - 4px)"
                      $minWidthAuto
                      $minWidthAutoMD
                      $minWidthAutoSM
                      selected={option === item}
                      onClick={() => setOption(item)}
                    >
                      Option {item}
                    </Tab>
                  ))}
                </TabsWrapper>
                <Disclaimer $m="8px 0 0" $color={lightTheme.lightGray}>
                  {OPTION_NOTES[option]}
                </Disclaimer>
              </div>
            )}
            {parts.ai?.summary && <P $m="0">{parts.ai.summary}</P>}
            {projection ? (
              <SuggestedPlan data={result} projection={projection} />
            ) : (
              <P $m="0" $bold>
                No option B for this location.
              </P>
            )}
            {parts.adjustments.length > 0 && (
              <Div $flex $column $gap="4px">
                {parts.adjustments.map((line) => (
                  <Disclaimer key={line} $m="0" $color={lightTheme.lightGray}>
                    {line}
                  </Disclaimer>
                ))}
              </Div>
            )}
            {projection && (
              <SuggestionComparison
                data={result}
                projection={projection}
                title={hasOptionB ? `Option ${option}` : 'AI suggestion'}
                comparison={parts.comparison}
              />
            )}
            <SuggestionDetails data={result} ai={parts.ai} />
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Amounts, months and dates are calculated from this location's last
              3 months. The text around them is written by AI. Applying only
              fills the form; nothing is saved until you save the plan.
            </Disclaimer>
          </Div>
        ) : null}
      </Div>
    </ModalPortal>
  );
};
